import React, { useEffect, useState } from 'react';
import { Money, ShowChart, TrendingUp, AccountBalanceWallet, ListAlt } from '@mui/icons-material';
import PieChartIcon from '@mui/icons-material/PieChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import MoneyIcon from '@mui/icons-material/Money'; // Ícone de dinheiro
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, PieChart, Pie, Cell } from 'recharts';
import Wallet from '../components/finance/Wallet'
import Releases from '../components/finance/Releases'
import axios from 'axios';
import GlobalLoading from '../components/GlobalLoading';

const COLORS = ['#818cf8', '#c084fc', '#f472b6', '#38bdf8', '#a78bfa'];

const Dashboard = () => {
    const [caixa, setCaixa] = useState(0);
    const [lucroBruto, setLucroBruto] = useState(0);
    const [receitas, setReceitas] = useState(0);
    const [despesas, setDespesas] = useState(0);
    const [lucrosPrejuizosMensais, setLucrosPrejuizosMensais] = useState([]);
    const [gastosEmpresa, setGastosEmpresa] = useState([]);
    const anoAtual = new Date().getFullYear();
    const [gastosMensais, setGastosMensais] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchDashboardData = async () => {
            setIsLoading(true);
            try {
                await Promise.all([
                    fetchSaldoCaixa(),
                    fetchLucroBruto(),
                    fetchLucrosPrejuizosMensais(),
                    fetchGastos()
                ]);
            } catch (err) {
                console.error("Erro ao carregar dados do dashboard:", err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    // Função para obter o saldo atual do caixa
    const fetchSaldoCaixa = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/walletRoutes/saldo`);
            const data = await response.json();
            setCaixa(data.saldo); // Atualiza o saldo com o valor do backend
        } catch (error) {
            console.error('Erro ao buscar saldo do caixa:', error);
        }
    };

    // Função para buscar lucro bruto, receitas e despesas
    const fetchLucroBruto = () => {
        fetch(`${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/reportsRoutes/lucro-bruto?ano=${anoAtual}`)
            .then((response) => response.json())
            .then((data) => {
                const { lucroBruto, receitas, despesas } = data;
                setLucroBruto(lucroBruto);
                setReceitas(parseFloat(receitas));
                setDespesas(parseFloat(despesas));
            })
            .catch((error) => {
                console.error('Erro ao buscar lucro bruto:', error);
            });
    };

    const fetchLucrosPrejuizosMensais = () => {
        // Buscar lucros ou prejuízos mensais
        axios.get(`${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/reportsRoutes/lucros-prejuizos-mensais?ano=${anoAtual}`)
            .then(response => {
                setLucrosPrejuizosMensais(response.data);
            })
            .catch(error => console.error('Erro ao buscar lucros/prejuízos mensais:', error));
    }

    const fetchGastos = () => {
        // Buscar gastos mensais
        axios.get(`${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/reportsRoutes/gastos-mensais?ano=${anoAtual}`)
            .then(response => {
                setGastosMensais(response.data);

                const distribuicao = response.data.map(item => ({
                    name: item.categoria,
                    value: Math.abs(parseFloat(item.valor))  // remove o sinal de negativo e converte para número
                }));

                setGastosEmpresa(distribuicao);
            })
            .catch(error => {
                console.error('Erro ao buscar gastos mensais:', error);
            });
    }

    // Função para formatar um valor monetário no formato brasileiro (R$)
    const formatarValorMonetario = (valor) => {
        // Verifica se o valor é um número válido
        return isNaN(valor)
            ? 'R$ 0,00'  // Caso o valor não seja um número válido
            : valor.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            });
    };

    // Função chamada após o lançamento, que atualiza o saldo
    const atualizarSaldoCaixa = (valorLancamento) => {
        setCaixa((prevSaldo) => prevSaldo + valorLancamento); // Atualiza o saldo com base no saldo anterior
    };

    return (
        <div className="p-4 md:p-8 animate-fade-in max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl text-white font-bold tracking-wide">Painel de Controle</h1>
            </div>

            {/* Primeira linha de boxes (3 boxes) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Box: Lucro Bruto */}
                <div className="glass-panel p-6 flex items-center bg-[#1b2336] border-white/5 shadow-xl hover:bg-[#202940]">
                    <div className="w-16 h-16 rounded-xl bg-blue-500/20 flex items-center justify-center mr-5 shadow-[0_0_15px_rgba(59,130,246,0.2)]">
                        <ShowChart className="text-3xl text-blue-400" />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-xs font-semibold text-gray-400 tracking-wider uppercase mb-1">Lucro Bruto</span>
                        <span className="text-2xl font-bold text-white mb-1">
                            {lucroBruto === 'NaN' || lucroBruto === 'R$ NaN' || String(lucroBruto).includes('NaN') ? 'R$ 0,00' : lucroBruto || 'R$ 0,00'}
                        </span>
                    </div>
                </div>

                {/* Box: Receitas */}
                <div className="glass-panel p-6 flex items-center bg-[#1b2336] border-white/5 shadow-xl hover:bg-[#202940]">
                    <div className="w-16 h-16 rounded-xl bg-green-500/20 flex items-center justify-center mr-5 shadow-[0_0_15px_rgba(16,185,129,0.2)]">
                        <TrendingUp className="text-3xl text-green-400" />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-xs font-semibold text-gray-400 tracking-wider uppercase mb-1">Receitas</span>
                        <span className="text-2xl font-bold text-white mb-1">
                            {receitas === 'NaN' || String(receitas).includes('NaN') ? 'R$ 0,00' : formatarValorMonetario(receitas) || 'R$ 0,00'}
                        </span>
                    </div>
                </div>

                {/* Box: Despesas */}
                <div className="glass-panel p-6 flex items-center bg-[#1b2336] border-white/5 shadow-xl hover:bg-[#202940]">
                    <div className="w-16 h-16 rounded-xl bg-red-500/20 flex items-center justify-center mr-5 shadow-[0_0_15px_rgba(239,68,68,0.2)]">
                        <AccountBalanceWallet className="text-3xl text-red-400" />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-xs font-semibold text-gray-400 tracking-wider uppercase mb-1">Despesas</span>
                        <span className="text-2xl font-bold text-white mb-1">
                            {despesas === 'NaN' || String(despesas).includes('NaN') ? 'R$ 0,00' : formatarValorMonetario(despesas) || 'R$ 0,00'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Segunda linha (gráficos e listagem) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
                {/* Box de Gráficos */}
                <div className="col-span-1 lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

                    {/* Gráfico de Lucro Líquido Mensal */}
                    <div className="glass-panel p-6 space-y-6 bg-[#1b2336] border-white/5 shadow-xl">
                        <div className="flex items-center mb-4 border-b border-white/5 pb-4">
                            <TrendingUp className="text-2xl text-purple-400 mr-3" />
                            <h2 className="text-sm font-semibold text-gray-200 uppercase tracking-wider">Lucros ou Prejuízos Mensais</h2>
                        </div>
                        <ResponsiveContainer width="100%" height={250}>
                            <AreaChart data={lucrosPrejuizosMensais} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorLucro" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#818cf8" stopOpacity={0.4} />
                                        <stop offset="95%" stopColor="#818cf8" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                                <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                <Tooltip
                                    formatter={(value) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    contentStyle={{ backgroundColor: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }}
                                    itemStyle={{ color: '#e2e8f0' }}
                                />
                                <ReferenceLine y={0} stroke="rgba(255,255,255,0.1)" />
                                <Area
                                    type="monotone"
                                    dataKey="lucroPrejuizo"
                                    name="Lucro/Prejuízo"
                                    stroke="#818cf8"
                                    strokeWidth={3}
                                    fillOpacity={1}
                                    fill="url(#colorLucro)"
                                    dot={{ fill: '#818cf8', strokeWidth: 2, r: 3 }}
                                    activeDot={{ r: 6 }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Gráfico de Receitas e Despesas Mensais */}
                    <div className="glass-panel p-6 space-y-6 bg-[#1b2336] border-white/5 shadow-xl">
                        <div className="flex items-center mb-4 border-b border-white/5 pb-4">
                            <BarChartIcon className="text-2xl text-blue-400 mr-3" />
                            <h2 className="text-sm font-semibold text-gray-200 uppercase tracking-wider">Receitas vs Despesas</h2>
                        </div>

                        <div style={{ minWidth: '300px', width: '100%' }}>
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart
                                    data={lucrosPrejuizosMensais}
                                    margin={{ top: 10, right: 10, left: -20, bottom: 20 }}
                                    barCategoryGap={20}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                                    <XAxis
                                        dataKey="mes"
                                        tick={{ fontSize: 11, fill: '#64748b' }}
                                        angle={-30}
                                        textAnchor="end"
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: '#64748b' }}
                                        axisLine={false}
                                        tickLine={false}
                                        tickFormatter={(value) =>
                                            value.toLocaleString('pt-BR', {
                                                style: 'currency',
                                                currency: 'BRL',
                                                maximumFractionDigits: 0,
                                            })
                                        }
                                    />
                                    <Tooltip
                                        formatter={(value) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                        contentStyle={{ backgroundColor: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }}
                                        itemStyle={{ color: '#e2e8f0' }}
                                    />
                                    <Bar dataKey="receitas" fill="#10b981" name="Receitas" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="despesas" fill="#ef4444" name="Despesas" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Gráfico de Gastos da Empresa */}
                    <div className="glass-panel p-6 space-y-6 bg-[#1b2336] border-white/5 shadow-xl">
                        <div className="flex items-center mb-4 border-b border-white/5 pb-4">
                            <PieChartIcon className="text-2xl text-pink-400 mr-3" />
                            <h2 className="text-sm font-semibold text-gray-200 uppercase tracking-wider">Distribuição de Gastos</h2>
                        </div>
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={gastosEmpresa}
                                    dataKey="value"
                                    nameKey="name"
                                    outerRadius={80}
                                    innerRadius={50}
                                    stroke="none"
                                >
                                    {gastosEmpresa.map((entry, index) => (
                                        <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    formatter={(value) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    contentStyle={{ backgroundColor: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                                    itemStyle={{ color: '#e2e8f0' }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Box de Lançamentos */}
                <div className="glass-panel p-6 lg:col-span-3">
                    <Releases atualizarSaldo={atualizarSaldoCaixa} fetchSaldo={fetchSaldoCaixa} fetchLucroBruto={fetchLucroBruto} fetchLucrosPrejuizosMensais={fetchLucrosPrejuizosMensais} fetchGastos={fetchGastos} />
                </div>
            </div>
            
            <GlobalLoading isActive={isLoading} />
        </div>
    );
};

export default Dashboard;
